<template>
  <div>
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Carregando...</span>
      </div>
    </div>

    <div v-else-if="!resolvedAnswer" class="text-center py-5">
      <div class="symbol symbol-50px mb-3 mx-auto">
        <div class="symbol-label bg-light">
          <i class="bi bi-clipboard text-muted fs-2x"></i>
        </div>
      </div>
      <h6 class="text-muted mb-2">Nenhum formulário de pré-consulta</h6>
      <p class="text-muted fs-7 mb-0">Este agendamento ainda não possui respostas de pré-consulta.</p>
    </div>

    <div v-else class="row g-4">
      <div class="col-12">
        <div class="card border-0 bg-light bg-opacity-50">
          <div class="card-body p-4">
            <div class="row">
              <div class="col-md-6">
                <label class="form-label fw-semibold text-muted">Formulário</label>
                <p class="mb-0 fw-bold">{{ displayFormName }}</p>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold text-muted">Respondido em</label>
                <p class="mb-0">{{ resolvedAnswer.answered_at ? formatDateTimeFull(resolvedAnswer.answered_at) : 'Pendente' }}</p>
              </div>
              <slot name="header-extra" :answer="resolvedAnswer"></slot>
            </div>
            <div class="mt-4" v-if="editable">
              <button
                v-if="!editing"
                type="button"
                class="btn btn-sm btn-light-primary"
                @click="startEditing">
                <i class="bi bi-pencil me-1"></i>
                {{ resolvedAnswer.answered_at ? 'Editar respostas' : 'Preencher formulário' }}
              </button>
              <button
                v-else
                type="button"
                class="btn btn-sm btn-light"
                :disabled="saving"
                @click="cancelEditing">
                Cancelar edição
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="card border-0">
          <div class="card-header bg-primary bg-opacity-10 border-bottom-0">
            <h6 class="card-title text-white mb-0 fw-bold">
              Respostas do Formulário
            </h6>
          </div>
          <div class="card-body">
            <div v-if="sortedQuestions.length > 0" class="row g-3">
              <div v-for="question in sortedQuestions" :key="question.id_pre_schedule_question" class="col-12">
                <div class="card card-bordered">
                  <div class="card-body p-4">
                    <div class="d-flex align-items-start">
                      <div class="symbol symbol-35px me-3">
                        <div class="symbol-label bg-light-primary text-primary fw-bold">
                          {{ question.question_order }}
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <div class="d-flex justify-content-between align-items-start mb-2">
                          <div class="d-flex align-items-center gap-2 flex-grow-1">
                            <h6 class="mb-0 text-gray-800 fw-semibold">{{ question.question_text }}</h6>
                            <span v-if="question.required" class="badge badge-danger">Obrigatória</span>
                          </div>
                          <div class="ms-2">
                            <slot
                              name="question-actions"
                              :question="question"
                              :formatted-answer="formatAnswer(question)"
                              :has-answer="Boolean(getAnswerValue(question.id_pre_schedule_question))"
                            ></slot>
                          </div>
                        </div>
                        <div class="d-flex flex-wrap gap-2 mb-3">
                          <span class="badge" :class="getQuestionTypeBadgeClass(question.question_type)">
                            {{ getQuestionTypeLabel(question.question_type) }}
                          </span>
                        </div>
                        <div class="mt-3 p-3 bg-light bg-opacity-50 rounded">
                          <div class="mb-1">
                            <strong class="text-primary">Resposta:</strong>
                          </div>
                          <div class="mt-2" v-if="editing">
                            <textarea
                              v-if="question.question_type === 'text'"
                              class="form-control form-control-solid"
                              rows="3"
                              v-model="formAnswers[question.id_pre_schedule_question]"
                              placeholder="Digite a resposta..."></textarea>

                            <div v-else-if="question.question_type === 'radio'" class="d-flex flex-wrap gap-2">
                              <button
                                v-for="option in normalizedOptions(question)"
                                :key="option.value"
                                type="button"
                                class="btn btn-sm"
                                :class="formAnswers[question.id_pre_schedule_question] === option.value ? 'btn-primary' : 'btn-light-primary'"
                                @click="formAnswers[question.id_pre_schedule_question] = option.value">
                                {{ option.label }}
                              </button>
                            </div>

                            <div v-else-if="question.question_type === 'checkbox'" class="d-flex flex-wrap gap-2">
                              <button
                                v-for="option in normalizedOptions(question)"
                                :key="option.value"
                                type="button"
                                class="btn btn-sm"
                                :class="isChecked(question, option.value) ? 'btn-primary' : 'btn-light-primary'"
                                @click="toggleCheckbox(question, option.value)">
                                {{ option.label }}
                              </button>
                            </div>

                            <span v-else-if="question.question_type === 'file'" class="text-muted fs-7">
                              Arquivos continuam disponíveis pelo link enviado ao paciente.
                            </span>
                          </div>
                          <div class="mt-2" v-else>
                            <button
                              v-if="isFileQuestion(question) && getAnswerValue(question.id_pre_schedule_question)"
                              type="button"
                              class="btn btn-sm btn-light-primary"
                              :disabled="openingFileId === question.id_pre_schedule_question"
                              @click="openFile(question)">
                              <span v-if="openingFileId === question.id_pre_schedule_question" class="spinner-border spinner-border-sm me-1"></span>
                              <i v-else class="bi bi-box-arrow-up-right me-1"></i>
                              {{ formatAnswer(question) }}
                            </button>
                            <span v-else-if="getAnswerValue(question.id_pre_schedule_question)" class="fw-normal">
                              {{ formatAnswer(question) }}
                            </span>
                            <span v-else class="text-muted fst-italic">
                              Não respondido
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="editing" class="d-flex justify-content-between align-items-center mt-4">
              <span class="text-danger fs-7">{{ formError }}</span>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="saving"
                @click="saveAnswers">
                <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                Salvar respostas
              </button>
            </div>
            <div v-else class="text-center py-5">
              <div class="symbol symbol-50px mb-3 mx-auto">
                <div class="symbol-label bg-light">
                  <i class="bi bi-question-circle text-muted fs-2x"></i>
                </div>
              </div>
              <p class="text-muted">Nenhuma pergunta encontrada</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../../helpers/http";
import Alert from "../../helpers/alert";
import moment from "moment";

export default {
  name: "PreScheduleAnswerView",
  emits: ['saved'],
  props: {
    answer: {
      type: Object,
      default: null,
    },
    questions: {
      type: Array,
      default: null,
    },
    formName: {
      type: String,
      default: null,
    },
    scheduleId: {
      type: [Number, String],
      default: null,
    },
    uniqueKey: {
      default: null,
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loadedAnswer: null,
      loading: false,
      openingFileId: null,
      editing: false,
      saving: false,
      formAnswers: {},
      formError: '',
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    resolvedAnswer() {
      return this.answer || this.loadedAnswer;
    },
    displayFormName() {
      return this.formName || this.resolvedAnswer?.form?.form_name || '-';
    },
    resolvedScheduleId() {
      return this.scheduleId || this.resolvedAnswer?.id_sched;
    },
    sortedQuestions() {
      const questions = this.questions || this.resolvedAnswer?.form?.questions || [];
      return [...questions].sort((a, b) => a.question_order - b.question_order);
    },
  },
  watch: {
    uniqueKey() {
      this.loadAnswer();
    },
    scheduleId() {
      this.loadAnswer();
    },
    resolvedAnswer() {
      this.resetForm();
    },
  },
  mounted() {
    this.loadAnswer();
    this.resetForm();
  },
  methods: {
    loadAnswer() {
      if (this.answer || !this.scheduleId || !this.place?.id_place) {
        return;
      }

      this.loading = true;
      this.loadedAnswer = null;

      http.get(`/places/${this.place.id_place}/schedules/${this.scheduleId}/pre-schedule-answer`)
        .then((response) => {
          const data = response.data;
          this.loadedAnswer = data?.id_pre_schedule_answer || data?.form ? data : null;
          this.resetForm();
        })
        .catch(() => {
          this.loadedAnswer = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    startEditing() {
      this.resetForm();
      this.editing = true;
    },
    cancelEditing() {
      this.resetForm();
      this.editing = this.editable && !this.resolvedAnswer?.answered_at;
    },
    resetForm() {
      const answers = this.resolvedAnswer?.answers || {};
      this.formAnswers = this.sortedQuestions.reduce((acc, question) => {
        const value = answers[question.id_pre_schedule_question] ?? answers[String(question.id_pre_schedule_question)];
        acc[question.id_pre_schedule_question] = question.question_type === 'checkbox'
          ? (Array.isArray(value) ? [...value] : [])
          : (value ?? '');
        return acc;
      }, {});
      this.formError = '';
      this.editing = this.editable && !this.resolvedAnswer?.answered_at;
    },
    saveAnswers() {
      const missing = this.sortedQuestions
        .filter((question) => question.required)
        .filter((question) => !this.hasFormAnswer(question))
        .map((question) => question.question_text);

      if (missing.length) {
        this.formError = `Preencha: ${missing.join(', ')}.`;
        return;
      }

      this.saving = true;
      this.formError = '';

      http.put(`/places/${this.place.id_place}/schedules/${this.resolvedScheduleId}/pre-schedule-answer`, {
        answers: this.formAnswers,
      }).then((response) => {
        this.loadedAnswer = response.data;
        this.editing = false;
        Alert.toastSuccess('Respostas salvas.');
        this.$emit('saved', response.data);
      }).catch((e) => {
        this.formError = e.response?.data?.message || 'Erro ao salvar respostas.';
      }).finally(() => {
        this.saving = false;
      });
    },
    hasFormAnswer(question) {
      const value = this.formAnswers[question.id_pre_schedule_question];
      if (question.question_type === 'file') {
        return value && typeof value === 'object' && value.id_file;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== undefined && String(value).trim() !== '';
    },
    normalizedOptions(question) {
      if (!Array.isArray(question.options)) {
        return [];
      }
      return question.options.map((option) => {
        if (typeof option === 'string') {
          return { label: option, value: option };
        }
        return {
          label: String(option.label || option.value || ''),
          value: String(option.value || option.label || ''),
        };
      }).filter((option) => option.label && option.value);
    },
    isChecked(question, value) {
      const values = this.formAnswers[question.id_pre_schedule_question];
      return Array.isArray(values) && values.includes(value);
    },
    toggleCheckbox(question, value) {
      const key = question.id_pre_schedule_question;
      const values = Array.isArray(this.formAnswers[key]) ? [...this.formAnswers[key]] : [];
      const index = values.indexOf(value);
      if (index === -1) {
        values.push(value);
      } else {
        values.splice(index, 1);
      }
      this.formAnswers[key] = values;
    },
    getAnswerValue(questionId) {
      const answers = this.resolvedAnswer?.answers;
      if (!answers || questionId == null) {
        return null;
      }

      return answers[questionId] ?? answers[String(questionId)] ?? null;
    },
    isFileQuestion(question) {
      return question?.question_type === 'file';
    },
    fileId(question) {
      const answerValue = this.getAnswerValue(question.id_pre_schedule_question);
      if (answerValue && typeof answerValue === 'object') {
        return answerValue.id_file || null;
      }
      return null;
    },
    formatAnswer(question) {
      const answerValue = this.getAnswerValue(question.id_pre_schedule_question);
      if (!answerValue) {
        return '';
      }

      if (this.isFileQuestion(question)) {
        if (typeof answerValue === 'string') {
          return answerValue;
        }
        return answerValue.file_name || answerValue.fileName || 'Arquivo enviado';
      }

      if (question.question_type === 'checkbox' && Array.isArray(answerValue)) {
        return answerValue.join(', ');
      }

      if (question.question_type === 'radio' && Array.isArray(question.options)) {
        const option = question.options.find((opt) => {
          if (typeof opt === 'string') {
            return opt === answerValue;
          }
          return opt.value === answerValue || opt.label === answerValue;
        });
        if (!option) {
          return answerValue;
        }
        return typeof option === 'string' ? option : option.label;
      }

      return answerValue;
    },
    openFile(question) {
      if (!this.fileId(question)) {
        Alert.toastWarning('Arquivo não disponível para visualização.');
        return;
      }

      if (!this.place?.id_place || !this.resolvedScheduleId) {
        Alert.toastError('Não foi possível localizar o agendamento deste arquivo.');
        return;
      }

      this.openingFileId = question.id_pre_schedule_question;

      http.get(`/places/${this.place.id_place}/schedules/${this.resolvedScheduleId}/pre-schedule-answer/files/${question.id_pre_schedule_question}`)
        .then((response) => {
          const data = response.data;
          const binary = atob(data.file_base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: data.file_mime_type || 'application/octet-stream' });
          const fileUrl = URL.createObjectURL(blob);
          window.open(fileUrl, '_blank');
          setTimeout(() => URL.revokeObjectURL(fileUrl), 60000);
        })
        .catch(() => {
          Alert.toastError('Não foi possível abrir o arquivo.');
        })
        .finally(() => {
          this.openingFileId = null;
        });
    },
    getQuestionTypeLabel(type) {
      const labels = {
        text: 'Texto',
        radio: 'Escolha Única',
        checkbox: 'Múltipla Escolha',
        file: 'Arquivo',
      };
      return labels[type] || type;
    },
    getQuestionTypeBadgeClass(type) {
      const classes = {
        text: 'badge-light-primary',
        radio: 'badge-light-info',
        checkbox: 'badge-light-success',
        file: 'badge-light-warning',
      };
      return classes[type] || 'badge-light-secondary';
    },
    formatDateTimeFull(dateTime) {
      if (!dateTime) {
        return '-';
      }
      return moment(dateTime).format('DD/MM/YYYY HH:mm');
    },
  },
};
</script>

<style scoped>
.card-bordered {
  border: 1px solid var(--bs-border-color);
  border-radius: 0.75rem;
  box-shadow: 0 0.5rem 1.5rem 0.5rem rgba(0, 0, 0, 0.075);
}

.text-gray-800 {
  color: #3f4254 !important;
}

.fs-7 {
  font-size: 0.875rem;
}

.symbol-35px {
  width: 35px;
  height: 35px;
}

.symbol-50px {
  width: 50px;
  height: 50px;
}

.fs-2x {
  font-size: 2rem;
}

.bg-light-primary {
  background-color: #f1faff !important;
}

.badge-danger {
  background-color: #f1416c;
  color: #fff;
}

.fst-italic {
  font-style: italic;
}
</style>
